import React from "react";
import { useTheme } from "../../contexts/ThemeContext";

const Footer = () => {
    const { colors, isDark } = useTheme();
    const currentYear = new Date().getFullYear();

    const footerLinks = [
        {
            title: "Product",
            links: [
                { label: "Download", href: "https://play.google.com/store/apps/details?id=com.anonymousaliens.cu" },
                { label: "Premium", href: "/premium" },
                { label: "Why Ads?", href: "/ads" },
            ]
        },
        {
            title: "Legal",
            links: [
                { label: "Privacy Policy", href: "/privacy_policy" },
                { label: "Terms of Service", href: "/terms_of_service" },
                { label: "Account Deletion", href: "/deleteAccount" },
            ]
        },
        {
            title: "Support",
            links: [
                { label: "Our Response", href: "/university_response" },
                { label: "Donate", href: "/donate" },
                { label: "Contact", href: "mailto:contact@collegia.app" },
            ]
        },
    ];

    return (
        <footer className={`relative py-16 px-6 ${
            isDark 
                ? 'bg-gradient-to-t from-black via-gray-950/50 to-transparent' 
                : 'bg-gradient-to-t from-gray-50 via-white to-transparent'
        }`}>
            {/* Top border gradient */}
            <div 
                className={`absolute top-0 left-0 right-0 h-px ${
                    isDark 
                        ? 'bg-gradient-to-r from-transparent via-white/10 to-transparent' 
                        : 'bg-gradient-to-r from-transparent via-gray-300 to-transparent'
                }`}
            />

            <div className="max-w-5xl mx-auto">
                {/* Main footer content */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-16">
                    {/* Brand column */}
                    <div className="col-span-2 md:col-span-1">
                        <a href="/" className="flex items-center gap-2.5 mb-4 hover:opacity-80 transition-opacity">
                            <img
                                src="/assets/developer_icon_1.jpg"
                                alt="AlienX logo"
                                className="w-8 h-8 rounded-lg"
                            />
                            <span className={`text-2xl font-semibold ${colors.text}`}>
                                Alien<span className="blink">X</span>
                            </span>
                        </a>
                        <p className={`text-sm ${colors.textMuted} mb-6 leading-relaxed`}>
                            Independent student developers building privacy-first tools for the academic community.
                        </p>
                        
                        {/* Social links - fixed centering */}
                        <div className="flex gap-3">
                            <a
                                href="https://github.com/AnonymousAliensX"
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`w-10 h-10 rounded-xl transition-all duration-300 hover:scale-105 flex items-center justify-center ${
                                    isDark 
                                        ? 'bg-white/[0.05] hover:bg-white/[0.1] text-gray-400 hover:text-white' 
                                        : 'bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900'
                                }`}
                                aria-label="GitHub"
                            >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                                </svg>
                            </a>
                            <a
                                href="https://play.google.com/store/apps/details?id=com.anonymousaliens.cu"
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`w-10 h-10 rounded-xl transition-all duration-300 hover:scale-105 flex items-center justify-center ${
                                    isDark 
                                        ? 'bg-white/[0.05] hover:bg-white/[0.1] text-gray-400 hover:text-white' 
                                        : 'bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900'
                                }`}
                                aria-label="Play Store"
                            >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                                </svg>
                            </a>
                            <a
                                href="https://apps.apple.com/in/app/collegia-academics-manager/id6757691482"
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`w-10 h-10 rounded-xl transition-all duration-300 hover:scale-105 flex items-center justify-center ${
                                    isDark 
                                        ? 'bg-white/[0.05] hover:bg-white/[0.1] text-gray-400 hover:text-white' 
                                        : 'bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900'
                                }`}
                                aria-label="App Store"
                            >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z"/>
                                </svg>
                            </a>
                        </div>
                    </div>

                    {/* Link columns */}
                    {footerLinks.map((column, index) => (
                        <div key={index}>
                            <h4 className={`font-medium ${colors.text} mb-4 text-sm uppercase tracking-wider`}>
                                {column.title}
                            </h4>
                            <ul className="space-y-3">
                                {column.links.map((link, linkIndex) => (
                                    <li key={linkIndex}>
                                        <a
                                            href={link.href}
                                            target={link.href.startsWith('http') || link.href.startsWith('mailto') ? '_blank' : undefined}
                                            rel={link.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                                            className={`text-sm ${colors.textMuted} hover:${colors.text} transition-colors duration-300`}
                                        >
                                            {link.label}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                {/* Bottom bar */}
                <div className={`pt-8 border-t ${isDark ? 'border-white/[0.05]' : 'border-gray-200'}`}>
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                        <p className={`text-sm ${colors.textMuted}`}>
                            © {currentYear} Collegia. Built by students, for students.
                        </p>
                        
                        <div className="flex items-center gap-6">
                            <a 
                                href="/privacy_policy" 
                                className={`text-sm ${colors.textMuted} hover:${colors.text} transition-colors`}
                            >
                                Privacy
                            </a>
                            <a 
                                href="/terms_of_service" 
                                className={`text-sm ${colors.textMuted} hover:${colors.text} transition-colors`}
                            >
                                Terms
                            </a>
                            <a 
                                href="mailto:contact@collegia.app" 
                                className={`text-sm ${colors.textMuted} hover:${colors.text} transition-colors`}
                            >
                                Contact
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
